import React, { useState, useLayoutEffect, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  IconButton,
  CircularProgress,
  Button,
  useMediaQuery,
  Theme,
  Chip,
} from '@mui/material';
import {
  Send,
  MoreVert,
  KeyboardArrowUp,
  ArrowBack,
  AttachFile,
  EmojiEmotions,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ChatController } from '../../hooks/useChat';
import { useAuth } from '../../hooks/useAuth';
import { UserAvatar } from '../common/UserAvatar.tsx';
import { MessageItem } from './MessageItem';
import { TypingIndicator } from './TypingIndicator';
import { ChatEmptyState } from './ChatEmptyState';
import { ConversationFilesDialog } from './ConversationFilesDialog';
import { fileService } from '../../services/file.service';
import { toastService } from '../../services/toast.service';
import { getErrorMessage } from '../../utils/api-response.util';

const isSameDay = (d1: Date, d2: Date) => {
  return d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate();
};

const formatDateSeparator = (dateString: string) => {
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  if (isSameDay(date, today)) {
    return 'Сьогодні';
  } else if (isSameDay(date, yesterday)) {
    return 'Вчора';
  } else {
    return date.toLocaleDateString('uk-UA', { day: 'numeric', month: 'long', year: 'numeric' });
  }
};

export const ChatWindow: React.FC<{ chat: ChatController }> = ({ chat }) => {
  const [newMessage, setNewMessage] = useState('');
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [isFilesDialogOpen, setFilesDialogOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const {
    activeConversation,
    activeMessages,
    loadingMessages,
    sendingMessage,
    sendNewMessage,
    canLoadMoreMessages,
    loadMoreMessages,
    isConnected,
    handleTyping,
    typingUsers,
  } = chat;
  const { user } = useAuth();
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  useLayoutEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [activeMessages, typingUsers]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !activeConversation || sendingMessage || !isConnected) return;
    try {
      await sendNewMessage(activeConversation.id, { message: newMessage.trim(), conversationId: activeConversation.id });
      setNewMessage('');
    } catch (error) {
      console.error('Помилка відправлення повідомлення:', error);
    }
  };

  const handleFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !activeConversation || isUploadingFile) return;

    setIsUploadingFile(true);
    try {
      const uploaded = await fileService.uploadFile(file, undefined, 'chat');
      await sendNewMessage(activeConversation.id, {
        conversationId: activeConversation.id,
        fileId: uploaded.id,
      });
    } catch (error) {
      console.error('Помилка завантаження файлу:', error);
      toastService.error(getErrorMessage(error, 'Не вдалося завантажити файл'));
    } finally {
      setIsUploadingFile(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewMessage(e.target.value);
    handleTyping();
  };

  const handleLoadMore = () => {
    if (activeConversation && canLoadMoreMessages) {
      loadMoreMessages(activeConversation.id);
    }
  };

  const handleBack = () => {
    navigate('/chat');
  };

  const renderMessages = useMemo(() => {
    const items: React.ReactNode[] = [];
    
    for (let i = 0; i < activeMessages.length; i++) {
      const msg = activeMessages[i];
      const prevMsg = activeMessages[i - 1];

      const showDateSeparator = !prevMsg || !isSameDay(new Date(msg.sentAt), new Date(prevMsg.sentAt));
      if (showDateSeparator) {
        items.push(
          <Box key={`date-${msg.id}`} sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <Chip 
              label={formatDateSeparator(msg.sentAt)} 
              size="small" 
              sx={{ 
                backgroundColor: 'rgba(0, 0, 0, 0.05)', 
                color: 'text.secondary',
                fontSize: '0.75rem',
                height: 24
              }} 
            />
          </Box>
        );
      }

      const isFirstInGroup = !prevMsg || prevMsg.senderId !== msg.senderId || showDateSeparator;
      
      items.push(
        <MessageItem 
          key={msg.id} 
          message={msg} 
          isOwn={msg.senderId === user?.id} 
          showAvatar={isFirstInGroup}
        />
      );
    }
    return items;
  }, [activeMessages, user?.id]);

  if (!activeConversation) {
    return <ChatEmptyState />;
  }

  return (
    <Box display="flex" flexDirection="column" height="100%" sx={{ backgroundColor: '#fff', position: 'relative' }}>
      <Paper elevation={1} sx={{ 
        p: '10px 16px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderRadius: 0,
        zIndex: 10
      }}>
        <Box display="flex" alignItems="center" sx={{ minWidth: 0 }}>
          {isMobile && (
            <IconButton onClick={handleBack} sx={{ mr: 1 }}>
              <ArrowBack />
            </IconButton>
          )}
          <UserAvatar 
            path={activeConversation.participantAvatar} 
            name={activeConversation.participantName} 
            sx={{ mr: 1.5 }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              {activeConversation.participantName}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {activeConversation.participantStatus === 'online' ? 'В мережі' : 'Офлайн'}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={() => setFilesDialogOpen(true)}><MoreVert /></IconButton>
      </Paper>

      <Box 
        ref={messagesContainerRef} 
        sx={{ 
          flexGrow: 1, 
          overflowY: 'auto', 
          p: 2, 
          backgroundColor: '#f0f2f5', 
          backgroundImage: 'url("https://www.transparenttextures.com/patterns/subtle-white-feathers.png")',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {canLoadMoreMessages && (
          <Box textAlign="center" mb={2}>
            <Button 
              variant="text" 
              size="small" 
              onClick={handleLoadMore} 
              disabled={loadingMessages} 
              startIcon={loadingMessages ? <CircularProgress size={14} /> : <KeyboardArrowUp />}
              sx={{ borderRadius: 20, textTransform: 'none' }}
            >
              {loadingMessages ? 'Завантаження...' : 'Попередні повідомлення'}
            </Button>
          </Box>
        )}
        
        {loadingMessages && activeMessages.length === 0 && (
          <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
        )}
        
        {renderMessages}
        
        <Box sx={{ minHeight: '24px', mt: 1 }}>
           <TypingIndicator typingUsers={typingUsers} />
        </Box>

        {activeMessages.length === 0 && !loadingMessages && (
          <Box textAlign="center" p={4} sx={{ opacity: 0.6, mt: 'auto', mb: 'auto' }}>
            <Typography variant="body2">Тут поки що пусто.</Typography>
            <Typography variant="caption">Напишіть перше повідомлення!</Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ p: 2, backgroundColor: '#fff', borderTop: '1px solid #e0e0e0' }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'flex-end', 
          gap: 1,
          backgroundColor: '#f0f2f5',
          borderRadius: '24px',
          p: '8px 16px'
        }}>
          <input
            ref={fileInputRef}
            type="file"
            style={{ display: 'none' }}
            onChange={handleFileSelected}
          />
          <IconButton
            size="small"
            sx={{ color: 'text.secondary', mb: 0.5 }}
            disabled={isUploadingFile || !isConnected}
            onClick={() => fileInputRef.current?.click()}
          >
            {isUploadingFile ? <CircularProgress size={20} /> : <AttachFile />}
          </IconButton>

          <TextField
            fullWidth 
            multiline 
            maxRows={4} 
            placeholder={isConnected ? "Повідомлення..." : "З'єднання..."} 
            value={newMessage} 
            onChange={handleChange} 
            onKeyPress={handleKeyPress} 
            disabled={sendingMessage || !isConnected} 
            variant="standard"
            InputProps={{ disableUnderline: true }}
            sx={{ py: 0.5 }}
          />
          
          <IconButton size="small" sx={{ color: 'text.secondary', mb: 0.5 }}>
            <EmojiEmotions />
          </IconButton>

          <IconButton 
            color="primary" 
            onClick={handleSendMessage} 
            disabled={!newMessage.trim() || sendingMessage || !isConnected}
            sx={{ 
              backgroundColor: newMessage.trim() ? 'primary.main' : 'transparent',
              color: newMessage.trim() ? '#fff' : 'text.disabled',
              '&:hover': {
                backgroundColor: newMessage.trim() ? 'primary.dark' : 'transparent',
              },
              width: 40,
              height: 40,
              mb: -0.5
            }}
          >
            {sendingMessage ? <CircularProgress size={20} color="inherit" /> : <Send sx={{ fontSize: 20 }} />}
          </IconButton>
        </Box>
      </Box>

      <ConversationFilesDialog
        open={isFilesDialogOpen}
        onClose={() => setFilesDialogOpen(false)}
        conversationId={activeConversation.id}
      />
    </Box>
  );
};
